import { AlertCircle, CheckCircle2, TriangleAlert } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { SectionLabel } from "components/AppShell";
import { RichTextEditor } from "components/RichTextEditor";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";

import {
  DEFAULT_NOTIFICATION,
  describeInstant,
  notificationPlan,
  type NotificationPlan,
} from "../../after-event/after-event";
import {
  AlreadyBookedError,
  cancelNotification,
  CommandNotAppliedError,
  isAbortError,
  moveNotification,
  NoSendTimeError,
  readEventNotificationState,
  settledNotificationState,
  switchOnWithNotification,
  type NotificationState,
} from "../../after-event/after-event-notification";
import { useAfterEventSummary } from "../../after-event/use-after-event-summary";
import { eventEndsAt } from "../../track-switch/event-end";
import { AdminCommandFeedback } from "../AdminCommandFeedback";
import { templateProblem } from "../announcement-placeholders";
import type { EventSettings } from "../schemas";
import { useAdminCommand } from "../use-admin-command";
import { useEventSettingsWrite } from "../use-event-settings-writes";

/** The bounds firestore.rules puts on an announcement's text. */
const MAX_TITLE = 120;
const MAX_BODY = 1000;

function textProblem(title: string, body: string): string | null {
  if (!title.trim()) return "The notification needs a title.";
  if (!body.trim()) return "The notification needs a message.";
  if (title.trim().length > MAX_TITLE)
    return `The title must be ${MAX_TITLE} characters or fewer.`;
  if (body.trim().length > MAX_BODY)
    return `The message must be ${MAX_BODY} characters or fewer.`;
  return templateProblem(title) ?? templateProblem(body);
}

type Pending =
  | null
  | {
      direction: "on";
      state: NotificationState;
      plan: NotificationPlan;
    }
  | {
      direction: "off";
      /** Booked, or still being booked when the wait ran out. */
      state: Extract<NotificationState, { kind: "scheduled" | "pending" }>;
    };

/** The booking id a scheduled or still pending notification goes by. */
function bookingId(
  state: Extract<NotificationState, { kind: "scheduled" | "pending" }>,
): string {
  return state.kind === "scheduled" ? state.scheduledId : state.commandId;
}

/**
 * Switching the "After the event" section on or off (TNG-96).
 *
 * On opens a dialog that offers, pre-checked, to notify attendees when the
 * section goes live: a scheduled announcement for the instant the event ends,
 * or Send now once that has passed. The text is editable, and the booking is
 * an ordinary one, so the Announcements tab can edit, move or cancel it.
 *
 * The dialog first reads back, from a fresh copy of the event, the
 * notification a previous switch-on booked (after-event-notification.ts), and
 * offers a new one only when that one can no longer go out. Saving re-checks
 * in the transaction that books, so two organisers cannot both book one.
 *
 * Switching off while one is booked offers to cancel it, since it would
 * announce a section attendees can no longer see. A booking still being
 * applied is waited for briefly, then cancelled by its id if need be.
 */
/**
 * A signal that aborts when the component unmounts, so a command wait stops
 * polling Firestore for a dialog nobody is looking at any more.
 */
function useUnmountSignal(): () => AbortSignal | undefined {
  const controller = useRef<AbortController | null>(null);
  useEffect(() => {
    const current = new AbortController();
    controller.current = current;
    return () => current.abort();
  }, []);
  return useCallback(() => controller.current?.signal, []);
}

export function useAfterEventSwitch({
  eventId,
  uid,
  event,
  reload,
}: {
  eventId: string;
  uid: string;
  event: EventSettings;
  reload: () => void;
}): { request: (next: boolean) => void; busy: boolean; dialog: ReactNode } {
  const { save, saving, error: saveError } = useEventSettingsWrite(eventId);
  const [pending, setPending] = useState<Pending>(null);
  const [checking, setChecking] = useState(false);
  const [notify, setNotify] = useState(true);
  const [cancelToo, setCancelToo] = useState(true);
  const [title, setTitle] = useState<string>(DEFAULT_NOTIFICATION.title);
  const [body, setBody] = useState<string>(DEFAULT_NOTIFICATION.body);
  const [problem, setProblem] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const unmountSignal = useUnmountSignal();

  const busy = checking || working || saving;

  async function request(next: boolean) {
    setProblem(null);
    setChecking(true);
    let state: NotificationState;
    try {
      state = next
        ? await readEventNotificationState(eventId)
        : await settledNotificationState(eventId);
    } catch {
      // Unreadable is treated as nothing booked: the worst case is an offer
      // the organiser can untick.
      state = { kind: "none" };
    } finally {
      setChecking(false);
    }
    if (next) {
      setNotify(true);
      setTitle(DEFAULT_NOTIFICATION.title);
      setBody(DEFAULT_NOTIFICATION.body);
      setPending({
        direction: "on",
        state,
        plan: event.timezone
          ? notificationPlan(event.endDate, event.timezone, new Date())
          : {
              mode: "unavailable",
              problem:
                "The event has no time zone, so no send time can be worked out.",
            },
      });
      return;
    }
    if (state.kind === "scheduled" || state.kind === "pending") {
      setCancelToo(true);
      setPending({ direction: "off", state });
      return;
    }
    if (await save({ configAfterEvent: false })) reload();
  }

  const offersNotification =
    pending?.direction === "on" &&
    pending.state.kind === "none" &&
    pending.plan.mode !== "unavailable";

  async function confirmOn() {
    if (pending?.direction !== "on") return;
    setProblem(null);
    const sending = offersNotification && notify;
    if (sending) {
      const wrong = textProblem(title, body);
      if (wrong) {
        setProblem(wrong);
        return;
      }
    }
    setWorking(true);
    try {
      let ok: boolean;
      if (sending && pending.plan.mode !== "unavailable") {
        // One transaction: the command, the id and the switch. The send time
        // is worked out there from the end it reads, at this moment, since
        // the dialog may have sat open past the end or while another
        // organiser moved it.
        await switchOnWithNotification(
          eventId,
          uid,
          { title: title.trim(), body: body.trim() },
          new Date(),
        );
        ok = true;
      } else {
        ok = await save({ configAfterEvent: true });
      }
      if (ok) {
        setPending(null);
        reload();
      }
    } catch (error) {
      if (error instanceof AlreadyBookedError) {
        // Show what the other organiser booked; saving again switches the
        // section on without a second notification.
        setPending({ ...pending, state: error.state });
        setProblem(error.message);
        return;
      }
      if (error instanceof NoSendTimeError) {
        setProblem(`Nothing was saved: ${error.message}`);
        return;
      }
      setProblem(
        error instanceof Error
          ? `Nothing was saved: ${error.message}`
          : "Nothing was saved.",
      );
    } finally {
      setWorking(false);
    }
  }

  async function confirmOff() {
    if (pending?.direction !== "off") return;
    setProblem(null);
    setWorking(true);
    let cancelled = false;
    try {
      if (cancelToo) {
        try {
          // Resolves once the trigger has applied the cancel, so the dialog
          // stays open until the notification is really off the schedule.
          await cancelNotification(eventId, uid, bookingId(pending.state), {
            signal: unmountSignal(),
          });
        } catch (error) {
          if (isAbortError(error)) return;
          // Nothing switched off. Show what the booking is now, since a
          // cancel filed against a booking still being applied can fail
          // while the booking itself lands a moment later.
          let state: NotificationState = pending.state;
          try {
            state = await readEventNotificationState(eventId);
          } catch {
            // Keep what the dialog showed.
          }
          const reason =
            error instanceof CommandNotAppliedError &&
            error.outcome === "timeout"
              ? "The cancel has not been applied yet."
              : `The notification could not be cancelled: ${
                  error instanceof Error ? error.message : "unknown error"
                }`;
          if (state.kind === "scheduled" || state.kind === "pending") {
            setPending({ direction: "off", state });
            setProblem(
              `${reason} The section is still on. Try again, or cancel it in the Announcements tab.`,
            );
          } else {
            setProblem(
              `${reason} It will not go out any more, but the section is still on. Switch it off again.`,
            );
          }
          return;
        }
        cancelled = true;
      }
      if (await save({ configAfterEvent: false })) {
        setPending(null);
        reload();
      } else if (cancelled) {
        setProblem(
          "The notification was cancelled, but the section could not be switched off, so it is still on. Switch it off again.",
        );
      }
    } finally {
      setWorking(false);
    }
  }

  const end =
    event.timezone && eventEndsAt(event.endDate, event.timezone)
      ? describeInstant(
          eventEndsAt(event.endDate, event.timezone) as Date,
          event.timezone,
        )
      : null;
  const errorText = problem ?? saveError;

  const dialog = (
    <>
      <Dialog
        open={pending?.direction === "on"}
        onOpenChange={(open) => !open && !busy && setPending(null)}
      >
        <DialogContent data-testid="after-event-on-dialog">
          <DialogHeader>
            <DialogTitle>Show &ldquo;After the event&rdquo;?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Attendees see it at the top of the Overview once the event has ended
            {end ? `, at ${end}` : ""}: your summary, the resources marked for
            after the event, and the signals summary PDF.
          </p>
          {pending?.direction === "on" && (
            <NotificationOffer
              state={pending.state}
              plan={pending.plan}
              timeZone={event.timezone}
              notify={notify}
              onNotify={setNotify}
              title={title}
              onTitle={setTitle}
              body={body}
              onBody={setBody}
              disabled={busy}
            />
          )}
          {errorText && (
            <p
              role="alert"
              className="flex items-center gap-2 text-sm text-destructive-text"
              data-testid="after-event-on-error"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              {errorText}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => setPending(null)}
            >
              Cancel
            </Button>
            <Button
              variant="cta"
              disabled={busy}
              onClick={() => void confirmOn()}
              data-testid="confirm-after-event-on"
            >
              {working || saving ? "Saving…" : "Switch on"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pending?.direction === "off"}
        onOpenChange={(open) => !open && !busy && setPending(null)}
      >
        <DialogContent data-testid="after-event-off-dialog">
          <DialogHeader>
            <DialogTitle>Hide &ldquo;After the event&rdquo;?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {pending?.direction === "off" && pending.state.kind === "pending"
              ? "A notification that the section is ready is still being booked"
              : "A notification that the section is ready is booked"}
            {pending?.direction === "off" &&
            pending.state.kind === "scheduled" &&
            pending.state.sendAt &&
            event.timezone
              ? ` for ${describeInstant(pending.state.sendAt, event.timezone)}`
              : ""}
            . With the section hidden it would point attendees at nothing.
          </p>
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-lit"
              checked={cancelToo}
              disabled={busy}
              onChange={(e) => setCancelToo(e.target.checked)}
              data-testid="after-event-cancel-notification"
            />
            Cancel that notification too
          </label>
          {errorText && (
            <p role="alert" className="text-sm text-destructive-text">
              {errorText}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => setPending(null)}
            >
              Keep it on
            </Button>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => void confirmOff()}
              data-testid="confirm-after-event-off"
            >
              Switch off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  return { request: (next) => void request(next), busy, dialog };
}

function NotificationOffer({
  state,
  plan,
  timeZone,
  notify,
  onNotify,
  title,
  onTitle,
  body,
  onBody,
  disabled,
}: {
  state: NotificationState;
  plan: NotificationPlan;
  timeZone: string | undefined;
  notify: boolean;
  onNotify: (next: boolean) => void;
  title: string;
  onTitle: (next: string) => void;
  body: string;
  onBody: (next: string) => void;
  disabled: boolean;
}) {
  if (state.kind !== "none") {
    return (
      <p
        className="rounded-md border border-border p-3 text-sm text-muted-foreground"
        data-testid="after-event-notify-existing"
      >
        {state.kind === "scheduled"
          ? `The notification that it is ready is already booked${
              state.sendAt && timeZone
                ? ` for ${describeInstant(state.sendAt, timeZone)}`
                : ""
            }. You can change it in the Announcements tab.`
          : state.kind === "pending"
            ? "The notification that it is ready is being booked. It will show in the Announcements tab."
            : "The notification that it is ready has already gone out."}
      </p>
    );
  }
  if (plan.mode === "unavailable") {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="after-event-notify-unavailable"
      >
        {plan.problem}
      </p>
    );
  }
  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <label className="flex min-h-11 items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          className="size-4 accent-lit"
          checked={notify}
          disabled={disabled}
          onChange={(e) => onNotify(e.target.checked)}
          data-testid="after-event-notify"
        />
        {plan.mode === "schedule"
          ? "Notify attendees when it goes live"
          : "Send attendees a notification now"}
      </label>
      {notify && (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="after-event-notify-title">Title</Label>
            <Input
              id="after-event-notify-title"
              testId="after-event-notify-title"
              value={title}
              maxLength={MAX_TITLE}
              disabled={disabled}
              onChange={(e) => onTitle(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="after-event-notify-body">Message</Label>
            <Textarea
              id="after-event-notify-body"
              data-testid="after-event-notify-body"
              value={body}
              maxLength={MAX_BODY}
              disabled={disabled}
              onChange={(e) => onBody(e.target.value)}
            />
          </div>
          <p
            className="text-xs text-muted-foreground"
            data-testid="after-event-notify-when"
          >
            {plan.mode === "schedule"
              ? `It goes out at ${
                  timeZone ? describeInstant(plan.sendAt, timeZone) : ""
                }, when the event ends. `
              : "The event has already ended, so it goes out straight away. "}
            {plan.mode === "schedule"
              ? "It then shows in the Announcements tab, where you can edit, move or cancel it. "
              : "It then shows in the Announcements tab with the others sent. "}
            Placeholders such as {"{first_name}"} work as they do there.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * What the organiser writes for after the event (TNG-96). The description
 * editor, saved through SAVE_AFTER_EVENT_SUMMARY so the trigger sanitises it
 * exactly as it does the description.
 */
export function AfterEventSummaryCard({
  eventId,
  uid,
}: {
  eventId: string;
  uid: string;
}) {
  const summary = useAfterEventSummary(eventId);
  const { state, submit, reset } = useAdminCommand();
  const [html, setHtml] = useState<string | null>(null);
  const stored =
    summary.status === "success" ? (summary.summary?.html ?? "") : null;
  const busy = state.status === "submitting" || state.status === "watching";

  // The editor is filled once the stored summary has loaded, and again after
  // a save lands, so it shows what the sanitiser kept.
  useEffect(() => {
    if (stored !== null) setHtml(stored);
  }, [stored]);

  useEffect(() => {
    if (state.status === "succeeded") summary.reload();
  }, [state.status, summary.reload]);

  return (
    <Card
      className="border-border shadow-none"
      data-testid="after-event-summary-card"
    >
      <CardContent className="space-y-3 pt-6">
        <SectionLabel>After the event: summary</SectionLabel>
        <p className="text-sm text-muted-foreground">
          Shown at the top of the attendees&apos; Overview once the event has
          ended and the &ldquo;After the event&rdquo; module is on. Mark files
          for it in the Resources tab.
        </p>
        {summary.status === "error" && (
          <p className="text-sm text-destructive-text">
            Could not load the summary: {summary.error}
          </p>
        )}
        {html === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <RichTextEditor
            value={html}
            onChange={setHtml}
            disabled={busy}
            testId="after-event-summary-editor"
          />
        )}
        <Button
          variant="secondary"
          onClick={() =>
            submit("SAVE_AFTER_EVENT_SUMMARY", eventId, uid, {
              html: html ?? "",
            })
          }
          disabled={busy || html === null || html === stored}
          data-testid="save-after-event-summary"
        >
          Save summary
        </Button>
        <AdminCommandFeedback
          state={state}
          onReset={reset}
          testId="after-event-summary"
        />
      </CardContent>
    </Card>
  );
}

/**
 * After the end date or time zone is saved: a booked "summary is ready"
 * notification keeps the send time it was booked with, so it would go out at
 * the old end. This says so and offers to move it to the new end with
 * UPDATE_SCHEDULED_ANNOUNCEMENT, keeping its text and form (TNG-96).
 */
export function AfterEventNotificationTimeCheck({
  eventId,
  uid,
  endDate,
  timeZone,
}: {
  eventId: string;
  uid: string;
  endDate: string;
  timeZone: string;
}) {
  const [booking, setBooking] = useState<Extract<
    NotificationState,
    { kind: "scheduled" | "pending" }
  > | null>(null);
  const [outcome, setOutcome] = useState<
    | { status: "idle" | "moving" | "moved" }
    | { status: "error"; message: string }
  >({ status: "idle" });
  const plan = notificationPlan(endDate, timeZone, new Date());
  const newEnd = eventEndsAt(endDate, timeZone);
  const unmountSignal = useUnmountSignal();

  useEffect(() => {
    let current = true;
    const show = (state: NotificationState) => {
      if (!current) return;
      setBooking(
        state.kind === "scheduled" || state.kind === "pending" ? state : null,
      );
    };
    readEventNotificationState(eventId)
      .then((state) => {
        show(state);
        // A booking still being applied is reported at once, then waited
        // for, so the move is offered once it has landed.
        if (current && state.kind === "pending")
          return settledNotificationState(eventId).then(show);
      })
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [eventId, endDate, timeZone]);

  if (!booking || !newEnd || booking.sendAt?.getTime() === newEnd.getTime())
    return null;

  if (booking.kind === "pending") {
    return (
      <div
        role="status"
        className="flex items-start gap-2 rounded-md border border-status-warning bg-status-warning/15 p-3 text-sm text-foreground"
        data-testid="after-event-notification-time"
      >
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <p className="min-w-0">
          The &ldquo;summary is ready&rdquo; notification is still being booked
          {booking.sendAt
            ? ` for ${describeInstant(booking.sendAt, timeZone)}`
            : ""}
          , the old end of the event. Once it shows in the Announcements tab,
          move it to the new end there.
        </p>
      </div>
    );
  }
  const scheduled = booking;

  async function move() {
    if (plan.mode !== "schedule") return;
    setOutcome({ status: "moving" });
    try {
      // Resolves once the trigger has applied the move.
      await moveNotification(eventId, uid, scheduled, plan.sendAt, {
        signal: unmountSignal(),
      });
      setOutcome({ status: "moved" });
    } catch (error) {
      if (isAbortError(error)) return;
      const reason =
        error instanceof CommandNotAppliedError && error.outcome === "timeout"
          ? "The move has not been applied yet"
          : `The notification could not be moved: ${
              error instanceof Error ? error.message : "unknown error"
            }`;
      setOutcome({
        status: "error",
        message: `${reason}. Check its send time in the Announcements tab before trying again.`,
      });
    }
  }

  return (
    <div
      role="status"
      className={`flex items-start gap-2 rounded-md border p-3 text-sm text-foreground ${
        outcome.status === "moved"
          ? "border-status-success bg-status-success/15"
          : "border-status-warning bg-status-warning/15"
      }`}
      data-testid="after-event-notification-time"
    >
      {outcome.status === "moved" ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      ) : (
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      )}
      <div className="min-w-0 space-y-2">
        {outcome.status === "moved" ? (
          <p>
            The &ldquo;summary is ready&rdquo; notification now goes out at{" "}
            {describeInstant(newEnd, timeZone)}. It shows in the Announcements
            tab.
          </p>
        ) : (
          <>
            <p>
              The &ldquo;summary is ready&rdquo; notification is still booked
              for{" "}
              {scheduled.sendAt
                ? describeInstant(scheduled.sendAt, timeZone)
                : "the old end"}
              , the old end of the event.{" "}
              {plan.mode === "schedule"
                ? "Move it to the new end, or change it in the Announcements tab."
                : "The new end is too close or too far to book; change or cancel it in the Announcements tab."}
            </p>
            {plan.mode === "schedule" && (
              <Button
                size="sm"
                variant="outline"
                disabled={outcome.status === "moving"}
                onClick={() => void move()}
                data-testid="move-after-event-notification"
              >
                Move it to {describeInstant(plan.sendAt, timeZone)}
              </Button>
            )}
            {outcome.status === "error" && (
              <p role="alert" className="text-destructive-text">
                {outcome.message}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
